import { createContext, RefObject, useCallback, useContext, useId, useMemo, useRef, useState } from 'react';
import { DropdownManagerOutput, DropdownManagerProps, Item } from './DropdownMenu.types';

export const DropdownMenuContext = createContext<DropdownManagerOutput | null>(null);

export function useDropdownMenuManager(props: DropdownManagerProps): DropdownManagerOutput {
  const [isOpen, setIsOpen] = useState(!!props?.initialState?.isOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /**
   * Placement of the dropdown content relative to the trigger
   */
  const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');

  // identifier to wire aria-controls from Trigger -> Content
  const contentId = useId();

  const items = useRef<Item[]>([]);
  const focusedIndexRef = useRef<number>(-1);

  /**
   * ROVING TABINDEX: set which item is "active" and move focus to it.
   *
   * - If there are no items, clear the focused index and exit.
   * - Clamp `nextIndex` into [0, items.length - 1] to avoid out-of-bounds.
   * - Make exactly one item tabbable (tabIndex=0) and all others not tabbable (tabIndex=-1).
   * - Store the active index and focus its DOM node if it exists.
   *
   * this is to make the tab navigation work correctly in the dropdown menu.
   */
  const setActiveIndex = useCallback((nextIndex: number) => {
    if (items.current.length === 0) {
      focusedIndexRef.current = -1;
      return;
    }
    const clamped = Math.max(0, Math.min(nextIndex, items.current.length - 1));
    items.current.forEach((entry, i) => entry.setTabIndex(i === clamped ? 0 : -1));
    focusedIndexRef.current = clamped;
    // focus the active item if it exists
    const node = items.current[clamped]?.ref.current;
    if (node) node.focus();
  }, []);

  const focusFirstItem = useCallback(() => {
    if (items.current.length === 0) return;
    setActiveIndex(0);
  }, [setActiveIndex]);

  const moveFocus = useCallback(
    (delta: number | 'start' | 'end') => {
      if (items.current.length === 0) return;
      if (delta === 'start') return setActiveIndex(0);
      if (delta === 'end') return setActiveIndex(items.current.length - 1);
      const current = focusedIndexRef.current >= 0 ? focusedIndexRef.current : 0;
      setActiveIndex((current + delta + items.current.length) % items.current.length);
    },
    [setActiveIndex],
  );

  const registerItem = useCallback((ref: RefObject<HTMLDivElement>, setTabIndex: (n: number) => void) => {
    const item: Item = { ref, setTabIndex };
    items.current.push(item);

    /**
     * make items not tabbable by default
     */
    setTabIndex(-1);

    return () => {
      const idx = items.current.indexOf(item);
      if (idx !== -1) items.current.splice(idx, 1);

      /**
       * if we remove the focus item, reset focus index
       */
      if (idx === focusedIndexRef.current) {
        focusedIndexRef.current = -1;
      }
    };
  }, []);

  const handleStateChange = useCallback(
    (next: boolean) => {
      props?.onStateChange?.(next);
      setIsOpen(next);
    },
    [props],
  );

  const open = useCallback(() => handleStateChange(true), [handleStateChange]);

  const defer = typeof queueMicrotask === 'function' ? queueMicrotask : (cb: () => void) => Promise.resolve().then(cb);

  const close = useCallback(() => {
    handleStateChange(false);
    // Restore focus to trigger after close (common a11y pattern)
    defer(() => {
      triggerRef.current?.focus?.();
    });
  }, [handleStateChange]);

  return useMemo(
    () => ({
      isOpen,
      toggleOpen: () =>
        setIsOpen((p) => {
          if (props.onStateChange) {
            props.onStateChange(!p);
          }
          return !p;
        }),
      open,
      close,
      triggerRef,
      registerItem,
      contentId,
      focusFirstItem,
      moveFocus,
      placement, // expose placement
      setPlacement, // expose setter
    }),
    [isOpen, placement],
  );
}

export function useDropdown() {
  const ctx = useContext(DropdownMenuContext);
  if (!ctx) throw new Error('DropdownMenu components must be used within <DropdownMenu>');
  return ctx;
}
